import { Hint } from '@/components/Explain';
import { Money } from '@/components/primitives';
import { cn } from '@/lib/cn';
import {
  formatDuration,
  formatTime,
  parseMysqlTime,
  type TimeZoneMode,
} from '@/lib/format';
import type { BetBundle } from '@/types';

/**
 * The bet's life on one axis: stake → payout → reversal, with delivery retries
 * overlaid. Time-scaled, so a 14-second round and a round that hung for an hour
 * look different at a glance.
 */

interface Event {
  at: Date;
  kind: 'bet' | 'win' | 'rollback' | 'retry';
  label: string;
  amount?: string | null;
  currency?: string | null;
  detail?: string;
  raw: string;
}

export function Timeline({ bundle, tz }: { bundle: BetBundle; tz: TimeZoneMode }) {
  const events = collect(bundle);
  if (events.length === 0) return null;

  const first = events[0].at.getTime();
  const last = events[events.length - 1].at.getTime();
  const span = Math.max(last - first, 1);
  // A single-instant bet would collapse every marker onto one dot; spread it.
  const degenerate = last === first;

  return (
    <div className="py-4">
      {/* The track is inset so a marker at 0% or 100% — which is every bet's
          first and last event — still has room for its dot and labels. */}
      <div className="relative mx-auto h-[62px]" style={{ width: 'calc(100% - 160px)' }}>
        <div className="absolute inset-x-0 top-[4px] h-px bg-hairline-strong" />

        {events.map((e, i) => {
          const pct = degenerate
            ? (i / Math.max(events.length - 1, 1)) * 100
            : ((e.at.getTime() - first) / span) * 100;
          return (
            <Marker
              key={`${e.kind}-${i}-${e.raw}`}
              event={e}
              pct={pct}
              tz={tz}
              meta={bundle.currencyMetadata.data}
            />
          );
        })}
      </div>

      <div className="mt-1 text-center">
        <span className="font-condensed text-[10px] uppercase tracking-wider text-text-dim">
          {degenerate ? 'all events at the same instant' : `total ${formatDuration(span)}`}
        </span>
        <span className="ml-2 font-mono text-[10px] text-text-faint">
          {formatTime(events[0].at, tz, true).slice(0, 10)}
        </span>
      </div>
    </div>
  );
}

const KIND = {
  bet: { color: 'bg-info', ring: 'ring-info/30', text: 'text-info' },
  win: { color: 'bg-ok', ring: 'ring-ok/30', text: 'text-ok' },
  rollback: { color: 'bg-warn', ring: 'ring-warn/30', text: 'text-warn' },
  retry: { color: 'bg-crit', ring: 'ring-crit/30', text: 'text-crit' },
} as const;

function Marker({
  event,
  pct,
  tz,
  meta,
}: {
  event: Event;
  pct: number;
  tz: TimeZoneMode;
  meta: BetBundle['currencyMetadata']['data'];
}) {
  const k = KIND[event.kind];

  return (
    <div
      className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
      style={{ left: `${pct}%` }}
    >
      <Hint
        content={
          <div className="space-y-1">
            <div className={cn('eyebrow', k.text)}>{event.label}</div>
            {event.amount && (
              <div className="font-mono text-[12px]">
                <Money amount={event.amount} meta={meta} currency={event.currency} />
              </div>
            )}
            {event.detail && <div className="text-[11.5px] text-text-dim">{event.detail}</div>}
            <div className="border-t border-hairline pt-1 font-mono text-[11px] text-text-dim">
              {event.raw}
            </div>
          </div>
        }
      >
        <button
          type="button"
          className={cn('h-[9px] w-[9px] rounded-full ring-4 ring-bg', k.color, 'hover:ring-[5px]')}
        />
      </Hint>
      <div className="mt-2 flex flex-col items-center gap-0.5">
        <span
          className={cn(
            'font-condensed text-[10px] font-semibold uppercase tracking-wider',
            k.text,
          )}
        >
          {event.kind}
        </span>
        <span className="tnum whitespace-nowrap font-mono text-[10px] text-text-faint">
          {formatTime(event.at, tz)}
        </span>
      </div>
    </div>
  );
}

function collect(bundle: BetBundle): Event[] {
  const events: Event[] = [];

  for (const t of bundle.transactions.data ?? []) {
    const at = parseMysqlTime(t.created_at);
    if (!at) continue;
    events.push({
      at,
      kind: t.action as Event['kind'],
      label: `${t.action} · trx ${t.id}`,
      amount: t.amount,
      currency: t.currency,
      raw: t.created_at,
    });
  }

  // Retry attempts sit on the same axis — this is where "the win was late" shows.
  for (const d of bundle.delayDelivery.data ?? []) {
    const at = parseMysqlTime(d.last_delivery_try ?? d.created_at);
    if (!at) continue;
    events.push({
      at,
      kind: 'retry',
      label: `${d.action} delivery · ${d.tries} tries`,
      detail: `${d.delivered ? 'delivered' : 'NOT delivered'}${
        d.last_try_result ? ` · last: ${d.last_try_result}` : ''
      }`,
      raw: String(d.last_delivery_try ?? d.created_at),
    });
  }

  return events.sort((a, b) => a.at.getTime() - b.at.getTime());
}
